import React, { useEffect } from 'react';
import {
  Row, Col, Empty, DatePicker,
} from 'antd';
import {
  PhoneTwoTone, HourglassTwoTone, LikeTwoTone, ClockCircleTwoTone, UpCircleTwoTone,
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import moment, { Moment } from 'moment';
import { Container, FilterArea, EmptyArea } from './Styled';
import Line from '../chart/line/Line';
import Number from '../chart/number/Number';
import Radar from '../chart/radar/Radar';
import Indicator from '../chart/indicator/Indicator';

import { RootState } from '../../../model/rootReducer';
import { createLoadingSelector } from '../../../business/selectors/loadingSelectors';
import { timespanToString, timespanToTotalHour } from '../../../helpers/dateFormatHelper';
import { RangeTimeStatistic } from '../../../model/statistic/types';
import { round } from '../../../helpers/numberHelper';
import Loader from '../../system/loader/Loader';
import { getDashboardStatisticAction, DateRange } from '../../../business/actions/statisticActions';

const { RangePicker } = DatePicker;

type DashboardProps = {
  className?: string;
};

export const Dashboard: React.FunctionComponent<DashboardProps> = ({
  className,
}) => {
  const dateFormat = 'YYYY-MM-DD HH:mm ZZ';
  const dispatch = useDispatch();
  const loadingSelector = createLoadingSelector(['GET_DASHBOARD_STATISTIC']);
  const isFetching = useSelector((state: RootState) => loadingSelector(state));
  const statistic = useSelector(
    (state: RootState) => state.statistic.dashboard,
  );
  const endHour = {
    hour: 23, minute: 59, second: 59, millisecond: 0,
  };
  const selectedBotId = useSelector((state: RootState) => state.bot.selectedBot);
  const endDate = moment().set(endHour);
  const startDate = moment().add(-7, 'days');

  useEffect(() => {
    dispatch(getDashboardStatisticAction.request(
      new DateRange(startDate.format(dateFormat), endDate.format(dateFormat)),
    ));
    return () => {
      // clear stat
    };
  }, [selectedBotId]);

  const onIntervalChange = (values: any) => {
    const startDate = (values[0] as Moment).set({
      hour: 0, minute: 0, second: 0, millisecond: 0,
    }).format(dateFormat);
    const endDate = (values[1] as Moment).set(endHour).format(dateFormat);
    dispatch(getDashboardStatisticAction.request(new DateRange(startDate, endDate)));
  };

  const formatRangeTimeStatistic = (statistics: RangeTimeStatistic[]) => {
    const datas = statistics.map((item, i) => ({
      x: `${moment(item.startTime, 'hh:mm:ss').format('HH:mm')}-${moment(item.endTime, 'hh:mm:ss').format('HH:mm')}`,
      y: item.callNumber,
    }));

    return [{
      id: 'rangeTimeStatistic',
      data: datas,
    }];
  };

  const formatDailyStatistic = (statistics: RangeTimeStatistic[]) => {
    const maxElementsToShow = 15;
    const datas = statistics.slice(Math.max(statistics.length - maxElementsToShow, 0)).map((item, i) => ({
      x: moment.utc(moment.duration(item.startTime).as('milliseconds')).format('DD/MM'),
      y: item.callNumber,
    }));

    return [{
      id: 'dailyStatistic',
      data: datas,
    }];
  };

  const StatContent = () => (
    <>
      <Row style={{ marginTop: '16px' }} gutter={[16, 16]}>
        <Col span={6}>
          <Number
            color={{
              '0%': '#108ee9',
              '100%': '#ab2cf4',
            }}
            title="Nombre d'appels"
            text={statistic ?.totalCallNumber.value.toString()}
            percent={100}
            Icon={(
              <PhoneTwoTone
                style={{ fontSize: '30px' }}
                twoToneColor="#ab2cf4"
              />
            )}
            ExtraInformation={<Indicator text={statistic ?.totalCallNumber.diff.toString() ?? ''} isIncrease={statistic!.totalCallNumber.diff >= 0} />}
          />
        </Col>
        <Col span={6}>
          <Number
            color={{
              '0%': '#108ee9',
              '100%': '#87D9FA',
            }}
            title="Temps de communication"
            text={timespanToTotalHour(statistic?.totalCommunicationTime.value)}
            subText="heure(s)"
            percent={100}
            Icon={(
              <HourglassTwoTone
                style={{ fontSize: '30px' }}
                twoToneColor="#108ee9"
              />
            )}
            ExtraInformation={<Indicator text={timespanToString(statistic ?.totalCommunicationTime.diff ?? '')} isIncrease={moment.duration(statistic!.totalCommunicationTime.diff).asMilliseconds() >= 0} />}
          />
        </Col>
        <Col span={6}>
          <Number
            color={{
              '0%': '#4DEB62',
              '100%': '#3BC486',
            }}
            title="CSAT"
            text="N/A"
            percent={100}
            Icon={(
              <LikeTwoTone
                style={{ fontSize: '30px' }}
                twoToneColor="#4DEB62"
              />
            )}
            ExtraInformation={<Indicator text="N/A" isIncrease={round(statistic!.csat.diff, 1) >= 0} />}
          />
        </Col>
        <Col span={6}>
          <Number
            color={{
              '0%': '#3426D1',
              '100%': '#7049E6',
            }}
            title="Durée moyenne de traitement"
            text={timespanToString(statistic ?.dmt.value ?? '')}
            subText="min"
            percent={100}
            Icon={(
              <ClockCircleTwoTone
                style={{ fontSize: '30px' }}
                twoToneColor="#7049E6"
              />
            )}
            ExtraInformation={<Indicator text={timespanToString(statistic!.dmt.diff)} isIncrease={moment.duration(statistic!.dmt.diff).asMilliseconds() >= 0} />}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={16}>
          <Line title="Volume d'appels journalier" data={formatDailyStatistic(statistic!.dailyStatistic)} />
        </Col>
        <Col span={8}>
          <Radar data={statistic!.intentDistribution} title="Répartition des appels" />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Number
                color={{
                  '0%': '#108ee9',
                  '100%': '#87D9FA',
                }}
                title="Appels transférés"
                text={statistic ?.totalCallTransferred.value.toString()}
                percent={100}
                Icon={(
                  <UpCircleTwoTone
                    style={{ fontSize: '30px' }}
                    twoToneColor="#108ee9"
                  />
                )}
                ExtraInformation={<Indicator text={statistic ?.totalCallTransferred.diff.toString() ?? ''} isIncrease={statistic!.totalCallTransferred.diff >= 0} />}
              />
            </Col>
          </Row>
          <Number
            color={{
              '0%': '#E03D5D',
              '100%': '#E6397E',
            }}
            title="Appels abandonnés par l'appelant"
            text={
              statistic?.totalCallHungupByUser.value.toString()
              }
            percent={100}
            Icon={(
              <PhoneTwoTone
                style={{ fontSize: '30px' }}
                twoToneColor="#E03D5D"
              />
            )}
            ExtraInformation={<Indicator text={statistic?.totalCallHungupByUser.diff.toString() ?? ''} isIncrease={statistic!.totalCallHungupByUser.diff >= 0} />}
          />
        </Col>
        <Col span={18}>
          <Line title="Volume d'appels par tranches horaires" color="#b61ff2" data={formatRangeTimeStatistic(statistic!.hourlyStatistic)} />
        </Col>
      </Row>
    </>
  );

  function disabledFutureDate(current: any) {
    return current && current > moment().endOf('day');
  }

  return (
    <Loader isLoading={isFetching}>
      <Container className={className}>
        <FilterArea>
          { /*
        // @ts-ignore */}
          <RangePicker defaultValue={[startDate, endDate]} disabledDate={disabledFutureDate} onChange={onIntervalChange} style={{ float: 'right' }} />
        </FilterArea>
        {
          statistic ? <StatContent /> : <EmptyArea><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Les statistiques ne sont pas disponibles pour les dates sélectionnées" /></EmptyArea>
        }
      </Container>
    </Loader>
  );
};

export default Dashboard;
